import { resolveClimate, climateTone } from "@/components/dashboard/command/marketClimate";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  CheckCircle2,
  Circle,
  Clock,
  Radio,
  ShieldAlert,
  Target,
} from "lucide-react";
import { useGoldPrice } from "@/hooks/useGoldPrice";
import { useEconomicCalendar } from "@/hooks/useEconomicCalendar";
import { useMarketStats } from "@/components/dashboard/island/hooks/useMarketStats";
import { useIntel } from "@/components/dashboard/terminal/DeskIntel";
import {
  PerformanceSpectrum,
  PriceLevels,
  StructureAlignment,
} from "@/components/dashboard/terminal/MarketStation";
import {
  LiquidityTracker,
  ProximityRadar,
  SessionPlaybook,
} from "@/components/dashboard/terminal/DeskTools";
import { VolatilityPulse } from "@/components/dashboard/terminal/VolatilityPulse";
import { PositionTracker } from "@/components/dashboard/terminal/PositionTracker";
import { SetupChartModal } from "@/components/dashboard/terminal/SetupChartModal";
import { NewsStrip } from "@/components/dashboard/desk/NewsStrip";
import { TelegramDeskButton } from "@/components/notifications/TelegramDeskButton";
import { Panel, SetupCard, SetupRadar, DeskWire, MacroShield, RiskCockpit, Checklist } from "@/components/dashboard/terminal/RoomWidgets";

import { useLiveSignals } from "@/hooks/useSignalsData";
import { useAuth } from "@/lib/auth-context";
import { useWeekendHold } from "@/hooks/useWeekendHold";
import type { LiveSignal } from "@/lib/dashboard-signals";

export const Route = createFileRoute("/_authenticated/admin/wip/trading-room")({
  head: () => ({
    meta: [
      { title: "Trading Room | XAU Pro Academy" },
      {
        name: "description",
        content:
          "Live gold operations room: session radar, active setup tracking, macro shield, risk sizing and the pre execution checklist.",
      },
      { property: "og:title", content: "Trading Room | XAU Pro Academy" },
      {
        property: "og:description",
        content: "Session radar, active setup tracking, macro shield and risk sizing for XAUUSD.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TradingRoomPage,
});

/* ------------------------------------------------------------------ */
/* Session radar                                                       */
/* ------------------------------------------------------------------ */

type SessionWindow = { name: string; startH: number; endH: number; note: string };

const SESSIONS: SessionWindow[] = [
  { name: "Asian Range", startH: 22, endH: 7, note: "Range formation. Low participation." },
  { name: "London", startH: 7, endH: 12, note: "Primary liquidity window." },
  { name: "London / New York Overlap", startH: 12, endH: 16, note: "Peak participation." },
  { name: "New York", startH: 16, endH: 21, note: "Continuation and closing flows." },
  { name: "Market Quiet", startH: 21, endH: 22, note: "Transition. No execution value." },
];

function inWindow(h: number, w: SessionWindow) {
  return w.startH <= w.endH ? h >= w.startH && h < w.endH : h >= w.startH || h < w.endH;
}

function useClock(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

function countdownTo(now: number, targetHourUtc: number) {
  const d = new Date(now);
  const target = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), targetHourUtc, 0, 0, 0),
  );
  if (target.getTime() <= now) target.setUTCDate(target.getUTCDate() + 1);
  return target.getTime() - now;
}

function hms(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

/* ------------------------------------------------------------------ */
/* Shared shell                                                        */
/* ------------------------------------------------------------------ */

/** 24h UTC session track with a live "now" marker. Pure presentation. */
function SessionTrack({ now }: { now: number }) {
  const d = new Date(now);
  const pos = ((d.getUTCHours() * 60 + d.getUTCMinutes()) / 1440) * 100;
  const segs = [
    { s: 0, e: 7, label: "Asia", tone: "var(--muted-foreground)", a: 18 },
    { s: 7, e: 12, label: "London", tone: "var(--gold)", a: 45 },
    { s: 12, e: 16, label: "Overlap", tone: "var(--gold)", a: 75 },
    { s: 16, e: 21, label: "New York", tone: "var(--gold)", a: 40 },
    { s: 22, e: 24, label: "", tone: "var(--muted-foreground)", a: 18 },
  ];
  return (
    <div className="mt-5">
      <div className="relative h-7">
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border/60" />
        {segs.map((g) => (
          <div
            key={g.s}
            className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full"
            style={{
              left: `${(g.s / 24) * 100}%`,
              width: `calc(${((g.e - g.s) / 24) * 100}% - 2px)`,
              background: `color-mix(in oklab, ${g.tone} ${g.a}%, transparent)`,
            }}
          />
        ))}
        <div className="absolute top-0 h-full" style={{ left: `${pos}%` }}>
          <div className="h-full w-px -translate-x-1/2 bg-foreground/80" />
          <div className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold shadow-[0_0_12px_var(--gold)]" />
        </div>
      </div>
      <div className="relative mt-1 h-3 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
        {segs.filter((g) => g.label).map((g) => (
          <span
            key={g.s}
            className="absolute hidden -translate-x-1/2 sm:inline"
            style={{ left: `${((g.s + g.e) / 2 / 24) * 100}%` }}
          >
            {g.label}
          </span>
        ))}
        <span className="absolute left-0">00</span>
        <span className="absolute right-0">24 UTC</span>
      </div>
    </div>
  );
}

const fmt = (n: number | null | undefined) => (n == null ? "..." : n.toFixed(2));


/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

type ViewKey = "radar" | "structure" | "risk";
const VIEWS: { key: ViewKey; label: string; icon: typeof Radio }[] = [
  { key: "radar", label: "Live Radar", icon: Radio },
  { key: "structure", label: "Structure", icon: Activity },
  { key: "risk", label: "Risk Cockpit", icon: ShieldAlert },
];

function TradingRoomPage() {
  const now = useClock();
  const { user } = useAuth();
  const { price } = useGoldPrice(15_000);
  const stats = useMarketStats();
  const { data: intel } = useIntel();
  const { events } = useEconomicCalendar();
  const { data: signals, loading } = useLiveSignals(user?.id);
  const weekend = useWeekendHold(1000);
  const [view, setView] = useState<ViewKey>("radar");

  const prevClose = stats.snapshot?.previousClose ?? null;
  const delta = price != null && prevClose ? price - prevClose : null;
  const pct = delta != null && prevClose ? (delta / prevClose) * 100 : null;

  const hUtc = new Date(now).getUTCHours();
  const session = SESSIONS.find((w) => inWindow(hUtc, w)) ?? SESSIONS[4];
  const nextBoundary = countdownTo(now, session.endH);

  const active = useMemo(
    () => (signals ?? []).filter((s) => s.tradeStatus !== "Closed"),
    [signals],
  );

  // Shared hysteresis resolver, identical to the Command Center.
  const tone = climateTone(resolveClimate(pct));

  return (
    <div className="relative min-h-screen" style={{ ["--tone" as string]: tone }}>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 45% at 50% -5%, color-mix(in oklab, var(--tone) 18%, transparent), transparent 70%)",
        }}
      />
      <div className="relative container mx-auto max-w-7xl space-y-5 px-4 py-6">
        <div className="flex justify-end">
          <TelegramDeskButton />
        </div>
        <CommandStrip
          session={session}
          countdown={weekend.closed ? weekend.msToOpen : nextBoundary}
          weekend={weekend.closed}
          price={price}
          delta={delta}
          pct={pct}
        />

        <SetupRadar
          signals={active}
          loading={loading}
          price={price}
          weekend={weekend.closed}
        />

        <div
          role="tablist"
          aria-label="Trading Room views"
          className="relative grid grid-cols-3 rounded-xl border border-border/60 bg-card/40 p-1 backdrop-blur-xl"
        >
          <span
            aria-hidden
            className="absolute inset-y-1 rounded-lg border border-gold/40 bg-gold-muted shadow-[0_0_24px_-8px_var(--gold)] transition-all duration-500 ease-out"
            style={{
              left: `calc(${VIEWS.findIndex((v) => v.key === view)} * (100% - 0.5rem) / 3 + 0.25rem)`,
              width: "calc((100% - 0.5rem) / 3)",
            }}
          />
          {VIEWS.map((v, i) => {
            const Icon = v.icon;
            return (
              <button
                key={v.key}
                role="tab"
                aria-selected={view === v.key}
                onClick={() => setView(v.key)}
                className={`relative z-10 flex items-center justify-center gap-2 rounded-lg px-2 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 sm:text-[11px] ${
                  view === v.key ? "text-gold" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <span className="hidden text-[9px] opacity-50 sm:inline">0{i + 1}</span>
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{v.label}</span>
              </button>
            );
          })}
        </div>

        {view === "radar" && (
          <div key="radar" className="animate-fade-in grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="space-y-5 lg:col-span-8">
              <SessionPlaybook nowMs={now} />
              <DeskWire reading={intel?.reading} />
            </div>
            <div className="space-y-5 lg:col-span-4">
              <MacroShield events={events} now={now} />
              <VolatilityPulse nowMs={now} />
              <ProximityRadar price={price} />
            </div>
          </div>
        )}
        {view === "structure" && (
          <div key="structure" className="animate-fade-in grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="space-y-5 lg:col-span-8">
              <LiquidityTracker price={price} />
              <StructureAlignment />
            </div>
            <div className="space-y-5 lg:col-span-4">
              <PriceLevels price={price} />
              <PerformanceSpectrum />
            </div>
          </div>
        )}
        {view === "risk" && (
          <div key="risk" className="animate-fade-in grid grid-cols-1 gap-5 lg:grid-cols-2">
            <RiskCockpit />
            <Checklist />
          </div>
        )}



        <p className="text-center text-[11px] text-muted-foreground">
          Operational workspace. Levels and readings are observational, not execution advice.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1. Command strip                                                    */
/* ------------------------------------------------------------------ */

function CommandStrip({
  session,
  countdown,
  price,
  delta,
  pct,
  weekend,
}: {
  weekend: boolean;
  session: SessionWindow;
  countdown: number;
  price: number | null;
  delta: number | null;
  pct: number | null;
}) {
  return (
    <section
      className="relative overflow-hidden rounded-2xl border bg-card/40 backdrop-blur-xl"
      style={{ borderColor: "color-mix(in oklab, var(--tone) 40%, var(--border))" }}
    >
      {/* Breathing gold glow in the upper area */}
      <div
        aria-hidden
        className="orion-hero-breath pointer-events-none absolute -top-24 -right-16 h-56 w-72 rounded-full blur-[80px]"
        style={{
          background:
            "radial-gradient(circle, color-mix(in oklab, var(--gold) 10%, transparent), transparent 70%)",
        }}
      />
      {/* Inset desk alert stays inside the card without competing with session status. */}
      <NewsStrip />
      <div className="relative p-5 pt-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold/60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
            </span>
            {weekend ? "Trading Room · Standby" : "Trading Room · Live"}
          </div>
          <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl">{weekend ? "Weekend Hold" : session.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {weekend ? "Market closed. Desk on standby until the Sunday reopen." : session.note}
          </p>
          <div className="mt-2 inline-flex items-center gap-2 font-mono text-xs tabular-nums text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {weekend ? "Market reopens in" : "Session changes in"} {hms(countdown)}
          </div>
        </div>
        <div className="flex w-full items-end justify-between gap-5 lg:w-auto lg:justify-end">
          <div className="text-left lg:text-right">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              XAUUSD
            </div>
            <div className="font-mono text-4xl font-light tabular-nums text-foreground sm:text-5xl">{fmt(price)}</div>
            <div className="font-mono text-sm tabular-nums" style={{ color: "var(--tone)" }}>
              {delta != null && pct != null
                ? `${delta >= 0 ? "+" : ""}${delta.toFixed(2)} (${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%) ${weekend ? "Friday close" : "today"}`
                : "..."}
            </div>
          </div>
        </div>
      </div>
      {!weekend && <SessionTrack now={Date.now()} />}
      </div>
    </section>
  );
}
